import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { App } from '@capacitor/app';

/**
 * Open an external URL using Capacitor's Browser plugin
 * Falls back to window.open for web (development only)
 */
export const openExternalUrl = async (url: string): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
  } else {
    // Fallback for development in web browser
    window.open(url, '_blank');
  }
};

/**
 * Close the in-app browser (if open)
 */
export const closeBrowser = async (): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    await Browser.close();
  }
};

/**
 * Save and share a JSON file using Capacitor's Filesystem and Share plugins
 */
export const saveAndShareJson = async (data: object, filename: string): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    // Write the file to the cache directory
    const result = await Filesystem.writeFile({
      path: filename,
      data: JSON.stringify(data, null, 2),
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });

    // Share the file
    await Share.share({
      title: 'Export Data',
      text: 'Your exported data from Smash or Pass',
      url: result.uri,
      dialogTitle: 'Share your data export',
    });
  } else {
    // Fallback for development in web browser
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Get the app's custom URL scheme for deep linking
 * This is used for OAuth redirects
 */
export const getAppScheme = (): string => {
  // Use the package ID as the scheme for Android deep links
  return 'app.smashorpassdating';
};

/**
 * Get the redirect URL for auth operations
 */
export const getAuthRedirectUrl = (path: string = ''): string => {
  if (Capacitor.isNativePlatform()) {
    // Use deep link scheme for native apps
    return `${getAppScheme()}://${path}`;
  }
  // Fallback for development
  return `${window.location.origin}/${path}`;
};

/**
 * Check if running on native platform
 */
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Listen for app URL open events (deep links)
 */
export const addAppUrlListener = (callback: (url: string) => void): (() => void) => {
  const listener = App.addListener('appUrlOpen', (event) => {
    callback(event.url);
  });

  return () => {
    listener.then(l => l.remove());
  };
};

/**
 * Listen for browser finished events
 */
export const addBrowserListener = (callback: () => void): (() => void) => {
  if (Capacitor.isNativePlatform()) {
    const listener = Browser.addListener('browserFinished', callback);
    return () => {
      listener.then(l => l.remove());
    };
  }
  return () => {};
};
